// Named sidebar data providers for the CRM app.
//
// CRM rows are pre-filtered HERE rather than by the engine's `visibleWhen`,
// because CRM gates on its own permission map (`useCrmPermissions` →
// /crm/users/me/permissions, resolved through `resolveCrmNavPermission`) plus a
// fail-OPEN active-object gate — a different source from the @clarix/rbac store
// the engine reads. Reusing `crmRowVisible` verbatim keeps that audited
// fail-closed behaviour exactly as it was.
//
// These providers also resolve their own active row: CRM uses "longest match
// wins" so only the deepest matching row lights up, which the engine's
// prefix matching can't express (see RowConfig.forceActive).

import { useMemo } from 'react';
import type { LucideIcon } from 'lucide-react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { resolveCrmNavPermission } from '@clarix/rbac';
import {
  MASTER_GROUPS,
  groupForCategoryCode,
  iconForCategoryCode,
  isMasterGroupId,
  useCategories,
  type MasterGroupId,
} from '@clarix/services-master-config';
import type {
  RowConfig,
  SectionConfig,
  SidebarProviderResult,
  SidebarSectionsResult,
} from '@clarix/shared-types';
import { useActiveCrmObjectNames } from '../../../core/api/crm-objects.api';
import { useCrmPermissions } from '../../../core/api/crm-permissions.api';
import {
  CRM_COMPLIANCE_LINKS,
  CRM_PANEL_SECTIONS,
  CRM_SETTINGS_LINKS,
  GROUP_EXTRA_LINKS,
  crmRowVisible,
  type CrmPanelLink,
} from '../crm-nav';
import { iconNameOf } from '../engine/icons';
import { useCrmUserCounts } from '../crm-user-counts';

/** "Longest match wins" — only the deepest matching row is active. */
function longestMatch(links: readonly CrmPanelLink[], pathname: string): string | null {
  let best: string | null = null;
  for (const l of links) {
    const matches = pathname === l.to || pathname.startsWith(l.to + '/');
    if (matches && (best === null || l.to.length > best.length)) best = l.to;
  }
  return best;
}

function toRow(l: CrmPanelLink, activeTo: string | null): RowConfig {
  return {
    id: l.to,
    label: l.label,
    icon: iconNameOf(l.icon) ?? 'Layers',
    to: l.to,
    // The provider owns active-state here; see the module header.
    forceActive: l.to === activeTo,
  };
}

/**
 * Rows for a static CRM panel (`crm-operations`, `crm-commission`,
 * `crm-reports`), bucketed into their `group` blocks in declaration order.
 * Ungrouped rows lead the panel; each named group renders under its own label,
 * and a group whose every row was filtered out disappears with its heading.
 */
function useCrmPanelSections(panelId: string): SidebarSectionsResult {
  const { pathname } = useLocation();
  const { map: crmPerms, loaded } = useCrmPermissions();
  const activeObjects = useActiveCrmObjectNames();

  return useMemo(() => {
    const links = (CRM_PANEL_SECTIONS[panelId] ?? []).filter((l) =>
      crmRowVisible(l, crmPerms, activeObjects, loaded),
    );
    const activeTo = longestMatch(links, pathname);

    const sections: SectionConfig[] = [];
    for (const l of links) {
      const label = l.group ?? null;
      const last = sections[sections.length - 1];
      if (last && (last.label ?? null) === label) last.rows!.push(toRow(l, activeTo));
      else
        sections.push({
          id: label ?? `${panelId}:ungrouped:${sections.length}`,
          label: label ?? undefined,
          // Groups sit 12px apart (the source panel's `space-y-3`); the first
          // needs no top gap of its own.
          spaced: sections.length > 0,
          rows: [toRow(l, activeTo)],
        });
    }
    return { sections, emptyText: 'No items available.' };
  }, [panelId, crmPerms, activeObjects, loaded, pathname]);
}

export function useCrmOperationsSections(): SidebarSectionsResult {
  return useCrmPanelSections('crm-operations');
}

export function useCrmCommissionSections(): SidebarSectionsResult {
  return useCrmPanelSections('crm-commission');
}

export function useCrmReportsSections(): SidebarSectionsResult {
  return useCrmPanelSections('crm-reports');
}

/* ────────────────────────── Master (settings + config catalogue) ────────────────────────── */

/**
 * The Master panel: the former `crm-settings` and `crm-compliance` rails as its
 * two leading groups, then one group per master-config domain — each with its
 * permission-gated extra links above its category rows.
 */
export function useCrmMasterSections(): SidebarSectionsResult {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const { data: categories, isLoading } = useCategories();
  const { map: crmPerms, loaded } = useCrmPermissions();
  const activeObjects = useActiveCrmObjectNames();

  // Ignore unknown ids so a link to a retired group (e.g. the old `?group=crm`)
  // falls back to the default instead of matching no categories.
  const rawGroupParam = searchParams.get('group') ?? '';
  const groupParam: MasterGroupId | '' = isMasterGroupId(rawGroupParam) ? rawGroupParam : '';
  const categoryParam = searchParams.get('category') ?? '';

  // Master-config CATEGORY rows are gated on the SAME key as the page they open,
  // fail-closed. They need their own gate because neither layer around them
  // provides one: `GET /config/categories` is authenticate-only ON PURPOSE
  // (entity forms read master VALUES for their dropdowns), and the panel's rail
  // gate is ANY-of a wide key set — so a DSR-only handler used to open this panel
  // and see the entire master catalogue, every row a dead link.
  const canViewMasterConfig =
    loaded &&
    !!crmPerms &&
    resolveCrmNavPermission('crm.masterconfig.read').some((k) => crmPerms[k] === true);

  // Only the master-config page renders a category, so only there may a category
  // row be active — otherwise the fallback below would phantom-light the first
  // category alongside the real active settings row.
  const onMasterConfig =
    pathname === '/crm/settings/master-config' ||
    pathname.startsWith('/crm/settings/master-config/');

  return useMemo(() => {
    const extraLinks = MASTER_GROUPS.flatMap((g) => GROUP_EXTRA_LINKS[g.id] ?? []);
    const activeTo = longestMatch(
      [...CRM_SETTINGS_LINKS, ...CRM_COMPLIANCE_LINKS, ...extraLinks],
      pathname,
    );

    // Mirror the master-config page's default selection so the sidebar
    // highlights the row the page actually renders: landing via the rail icon
    // has no `?category=`, and the page falls back to the FIRST category of the
    // effective group.
    let activeCategory = '';
    if (onMasterConfig && categories) {
      const effectiveGroup: MasterGroupId =
        groupParam ||
        (categoryParam ? groupForCategoryCode(categoryParam) : (MASTER_GROUPS[0]?.id ?? 'other'));
      const groupCategories = categories.filter(
        (c) => groupForCategoryCode(c.code) === effectiveGroup,
      );
      activeCategory =
        categoryParam && groupCategories.some((c) => c.code === categoryParam)
          ? categoryParam
          : (groupCategories[0]?.code ?? '');
    } else if (onMasterConfig) {
      activeCategory = categoryParam;
    }

    const gate = (links: readonly CrmPanelLink[]) =>
      links.filter((l) => crmRowVisible(l, crmPerms, activeObjects, loaded));

    const sections: SectionConfig[] = [];
    // Settings leads the panel, then Compliance. Both are static, so they render
    // immediately while the master categories below are still loading.
    for (const [label, links] of [
      ['Settings', gate(CRM_SETTINGS_LINKS)],
      ['Compliance', gate(CRM_COMPLIANCE_LINKS)],
    ] as const) {
      if (links.length === 0) continue;
      sections.push({
        id: label,
        // The panel's own header already reads "Settings".
        label: label === 'Settings' ? undefined : label,
        spaced: sections.length > 0,
        rows: links.map((l) => toRow(l, activeTo)),
      });
    }

    // One section per domain group: its config links (e.g. Approvals) lead,
    // above that group's master-config category rows.
    for (const g of MASTER_GROUPS) {
      const owned = canViewMasterConfig
        ? (categories ?? []).filter((c) => groupForCategoryCode(c.code) === g.id)
        : [];
      const links = gate(GROUP_EXTRA_LINKS[g.id] ?? []);
      if (owned.length === 0 && links.length === 0) continue;

      const rows: RowConfig[] = [
        ...links.map((l) => toRow(l, activeTo)),
        ...owned.map<RowConfig>((c) => ({
          id: c.code,
          label: c.name,
          // `?? 'Layers'` mirrors iconForCategoryCode's own fallback: an
          // unregistered component must degrade to a glyph, never to nothing.
          icon: iconNameOf(iconForCategoryCode(c.code) as unknown as LucideIcon) ?? 'Layers',
          to: `/crm/settings/master-config?group=${encodeURIComponent(
            groupForCategoryCode(c.code),
          )}&category=${encodeURIComponent(c.code)}`,
          forceActive: c.code === activeCategory,
          // An admin who knows the system searches by backend code, not label.
          searchTerms: [c.code],
        })),
      ];
      sections.push({ id: g.id, label: g.label, spaced: sections.length > 0, rows });
    }

    return {
      sections,
      // Only skeleton for someone who will actually get categories — otherwise
      // the panel flashes placeholder rows that resolve to nothing.
      isLoading: isLoading && canViewMasterConfig,
      loadingVariant: 'skeleton',
      emptyText: 'No categories found.',
    };
  }, [
    categories,
    isLoading,
    canViewMasterConfig,
    crmPerms,
    activeObjects,
    loaded,
    pathname,
    onMasterConfig,
    groupParam,
    categoryParam,
  ]);
}

/* ────────────────────────── Users ────────────────────────── */

/**
 * CRM per-status user counts. The CRM list endpoint excludes removed/deleted
 * users and has NO separate "invited" bucket, so "All" is simply
 * active + deactivated — matching the CRM Users page's own tabs.
 */
export function useCrmUsersProvider(): SidebarProviderResult {
  const { counts, isLoading, isError } = useCrmUserCounts();

  return {
    rows: [
      {
        id: 'all',
        label: 'All Users',
        icon: 'Users',
        toParam: { pathname: '/crm/users', param: 'status', value: 'all', isDefault: true },
        badge: { kind: 'count', tone: 'gray', value: counts.all },
      },
      {
        id: 'active',
        label: 'Active',
        icon: 'UserCheck',
        iconTone: 'emerald',
        toParam: { pathname: '/crm/users', param: 'status', value: 'active' },
        badge: { kind: 'count', tone: 'emerald', value: counts.active },
      },
      {
        id: 'inactive',
        label: 'Deactivated',
        icon: 'UserX',
        toParam: { pathname: '/crm/users', param: 'status', value: 'inactive' },
        badge: { kind: 'count', tone: 'gray', value: counts.deactive },
      },
    ],
    isLoading,
    isError,
  };
}
